import { Icon } from '@iconify/react';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TagsField } from '../Fields/TagsField';
import { ArticleForm } from '../hooks/useArticleSheet.hook';
import { ArticleDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useAutoRefresh } from '@/Domain/hooks/KnowledgeBase/useAutoRefresh.hook';
import { isRefreshableArticleType } from '@/Domain/utils/article';
import { cn } from '@/Domain/utils/cn';

interface ArticleMetadataProps {
  article: ArticleDto | undefined;
  form: UseFormReturn<ArticleForm>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const ArticleMetadata = ({
  article,
  form,
  onRefresh,
  isRefreshing = false,
}: ArticleMetadataProps) => {
  const { t } = useTranslation(['knowledge_base']);
  const { settings: autoRefreshSettings } = useAutoRefresh();

  const typeBadge = useMemo(() => {
    const type = article?.type;
    if (!type) return null;
    return (
      <Badge variant="secondary" size="md">
        {type}
      </Badge>
    );
  }, [article?.type]);

  const lastEditedDate = useMemo(() => {
    if (!article) return '-';
    const timestamp = article.last_updated_timestamp;
    return timestamp ? format(new Date(timestamp), 'PP') : '-';
  }, [article]);

  const createdDate = useMemo(() => {
    if (!article?.created_timestamp) return '-';
    return format(new Date(article.created_timestamp), 'PP');
  }, [article?.created_timestamp]);

  const lastRefreshedDate = useMemo(() => {
    if (!article?.last_updated_from_external_source_timestamp) return null;
    return format(new Date(article.last_updated_from_external_source_timestamp), 'PP');
  }, [article?.last_updated_from_external_source_timestamp]);

  const canRefresh = useMemo(() => {
    if (!article) return false;
    return isRefreshableArticleType(article.type) && !!article.url?.trim();
  }, [article]);

  const isEditedManually = !!article?.content_edited_manually_at;
  const showFreshnessStatus =
    canRefresh && !!autoRefreshSettings?.enabled && !!autoRefreshSettings?.available;

  const refreshButton = (
    <Button
      variant="secondary"
      size="sm"
      onClick={onRefresh}
      disabled={isRefreshing}
      className="w-full"
    >
      <Icon
        icon={isRefreshing ? 'mingcute:loading-line' : 'mingcute:refresh-2-line'}
        className={cn('mr-2 h-4 w-4', isRefreshing && 'animate-spin')}
      />
      {isRefreshing
        ? t('knowledge_base:metadata.refreshing')
        : t('knowledge_base:metadata.refresh_from_url')}
    </Button>
  );

  return (
    <TooltipProvider>
      <aside className="space-y-6 border-t border-border py-6 lg:border-t-0 lg:pr-4 lg:pl-6">
        <FieldLabel>{t('knowledge_base:metadata.title')}</FieldLabel>

        <div className="mt-3 space-y-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t('knowledge_base:metadata.type')}</span>
            {typeBadge}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t('knowledge_base:metadata.created')}</span>
            <span className="text-foreground">{createdDate}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {t('knowledge_base:metadata.last_edited')}
            </span>
            <span className="text-foreground">{lastEditedDate}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t('knowledge_base:metadata.url')}</span>
            {article?.url?.trim() ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={article.url ? article.url : ''}
                    target="_blank"
                    rel="noreferrer"
                    className="max-w-[60%] truncate text-foreground hover:cursor-pointer"
                  >
                    {article.url}
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{article.url}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="max-w-[60%] truncate text-foreground">-</span>
            )}
          </div>

          {lastRefreshedDate && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                {t('knowledge_base:metadata.last_refreshed')}
              </span>
              <span className="text-foreground">{lastRefreshedDate}</span>
            </div>
          )}
        </div>

        {showFreshnessStatus && (
          <div className="flex items-start gap-2" data-testid="article-freshness-status">
            <span
              className={cn(
                'mt-1 h-2 w-2 shrink-0 rounded-full',
                isEditedManually ? 'border border-muted-foreground' : 'bg-success',
              )}
            />
            <div className="space-y-0.5">
              <p className="text-xs font-medium text-foreground">
                {isEditedManually
                  ? t('knowledge_base:metadata.edited_manually')
                  : t('knowledge_base:metadata.auto_refresh_on')}
              </p>
              <p className="text-xs text-muted-foreground">
                {isEditedManually
                  ? t('knowledge_base:metadata.edited_manually_description')
                  : t('knowledge_base:metadata.auto_refresh_on_description')}
              </p>
            </div>
          </div>
        )}

        {canRefresh &&
          (isEditedManually ? (
            <Tooltip>
              <TooltipTrigger asChild>{refreshButton}</TooltipTrigger>
              <TooltipContent>
                <p>{t('knowledge_base:metadata.refresh_overwrites_edits')}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            refreshButton
          ))}

        <Separator />

        <TagsField form={form} articleId={article?.id} />
      </aside>
    </TooltipProvider>
  );
};
